<template>
	<div
		class="g-block-pay-file-collection"
		:class="{
			'g-block-pay-file-collection--edit': editable,
			'is-active': editable && active,
			'g-block-pay-file-collection--h5': h5,
			'g-block-pay-file-collection--compact': compact,
			'g-block-pay-file-collection--unlocked': isUnlocked,
		}"
		@click="onWrapClick"
	>
		<div class="g-block-pay-file-collection__card" :style="cardStyle">
			<div class="g-block-pay-file-collection__header">
				<div class="g-block-pay-file-collection__folder" aria-hidden="true">
					<div class="g-block-pay-file-collection__folder-body">
						<img
							class="g-block-pay-file-collection__folder-icon"
							:src="collectionFolderIcon"
							alt=""
						/>
					</div>
				</div>
				<div class="g-block-pay-file-collection__header-info">
					<div class="g-block-pay-file-collection__title">{{ collectionData.title }}</div>
					<div class="g-block-pay-file-collection__meta">{{ metaText }}</div>
				</div>
			</div>

			<div v-if="!isUnlocked && showInfoBar" class="g-block-pay-file-collection__info">
				<EyeOutlined class="g-block-pay-file-collection__info-icon" />
				<span>
					解锁后可查看全部
					<strong>{{ totalCount }}</strong>
					份文档
				</span>
			</div>

			<div v-if="listEntries.length" class="g-block-pay-file-collection__list">
				<div
					v-for="entry in visibleEntries"
					:key="entry.key"
					class="g-block-pay-file-collection__item"
					:class="{ 'is-locked': entry.locked }"
					@click.stop="onItemClick(entry)"
				>
					<div
						class="g-block-pay-file-collection__file-icon"
						:class="`is-${getItemTone(entry.item)}`"
						aria-hidden="true"
					>
						<img
							class="g-block-pay-file-collection__file-icon-img"
							:src="getItemIconUrl(entry.item)"
							:alt="getItemMark(entry.item)"
						/>
					</div>
					<div class="g-block-pay-file-collection__item-main">
						<div class="g-block-pay-file-collection__item-head">
							<span class="g-block-pay-file-collection__item-index">{{ formatIndex(entry.index) }}</span>
							<div class="g-block-pay-file-collection__item-title">{{ entry.item.title }}</div>
						</div>
						<div class="g-block-pay-file-collection__item-meta">{{ getItemMeta(entry.item) }}</div>
						<span
							v-if="!entry.locked && !isUnlocked"
							class="g-block-pay-file-collection__free-tag"
						>免费查看</span>
					</div>
					<div class="g-block-pay-file-collection__item-action">
						<button
							v-if="!entry.locked"
							type="button"
							class="g-block-pay-file-collection__view-btn"
							:class="{ 'is-readonly': !canViewItem(entry) }"
							@click.stop="onViewItem(entry, $event)"
						>
							<EyeOutlined />
							<span>查看</span>
							<RightOutlined />
						</button>
						<button
							v-else
							type="button"
							class="g-block-pay-file-collection__lock-btn"
							@click.stop="onUnlockClick"
						>
							<i class="mbm-iconfont mbm-suoding" aria-hidden="true"></i>
							<span>解锁</span>
						</button>
					</div>
				</div>
			</div>
			<div v-if="!isUnfolded && collectionData.initExpandCount > 0 && totalCount > visibleEntries.length" class="g-block-pay-file-collection__fold-btn-container">
				<button
					type="button"
					class="g-block-pay-file-collection__fold-btn"
					@click.stop="onFoldToggle"
				>
					<span>展开全部 {{ totalCount }} 份文档 </span>
					<i class="mbm-iconfont mbm-xiajiantou1  mbm-xiajiantou1111"></i>
				</button>
			</div>

			<div v-if="!isUnlocked && !shouldHidePrice" class="g-block-pay-file-collection__pay">
				<div class="g-block-pay-file-collection__pay-main">
					<div class="g-block-pay-file-collection__price-box">
						<div class="g-block-pay-file-collection__price">
							<span class="g-block-pay-file-collection__currency">{{ collectionData.currency }}</span>
							<span class="g-block-pay-file-collection__price-value">{{ collectionData.price }}</span>
						</div>
						<div class="g-block-pay-file-collection__pay-desc">{{ unlockHint }}</div>
					</div>
					<button
						type="button"
						class="g-block-pay-file-collection__unlock-btn"
						:style="buttonStyle"
						@click.stop="onUnlockClick"
					>
						<i class="mbm-iconfont mbm-suoding" aria-hidden="true"></i>
						<span>{{ collectionData.buttonText }}</span>
					</button>
				</div>
				<div class="g-block-pay-file-collection__features">
					<span
						v-for="feature in collectionData.footerFeatures"
						:key="feature.id"
						class="g-block-pay-file-collection__feature"
						:class="`is-${feature.icon}`"
					>
						<i :class="featureIconName(feature.icon)"></i>
						<span>{{ feature.text }}</span>
					</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import { geditPayUnlockMixin } from '../../../mixins/geditPayUnlock.js';
	import { EyeOutlined, RightOutlined, DownOutlined } from '@ant-design/icons-vue';
	import {
		formatPayFileCollectionIndex,
		getPayFileCollectionCardStyle,
		getPayFileCollectionFileIconUrl,
		getPayFileCollectionFileType,
		getPayFileCollectionFileUrl,
		getPayFileCollectionItemMeta,
		getPayFileCollectionListEntries,
		getPayFileCollectionMetaText,
		getPayFileCollectionUnlockHint,
		isPayFileCollectionUnlocked,
		normalizePayFileCollectionData,
		PAY_FILE_COLLECTION_COMPACT_FREE_COUNT,
		setPayFileCollectionUnlocked,
	} from '../../../utils/payFileCollection.js';

	export default {
		name: 'GPayFileCollectionShell',
		mixins: [geditPayUnlockMixin],
		components: { EyeOutlined, RightOutlined, DownOutlined },
		props: {
			data: {
				type: Object,
				required: true,
			},
			blockId: {
				type: String,
				default: '',
			},
			editable: {
				type: Boolean,
				default: false,
			},
			active: {
				type: Boolean,
				default: false,
			},
			h5: {
				type: Boolean,
				default: false,
			},
			compact: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['select'],
		data() {
			return {
				unlockedLocal: false,
				unfoldedLocal: false,
			};
		},
		computed: {
			collectionData() {
				return normalizePayFileCollectionData(this.data);
			},
			collectionFolderIcon() {
				return getPayFileCollectionFileIconUrl(this.collectionData.title);
			},
			cardStyle() {
				return getPayFileCollectionCardStyle(this.collectionData, { h5: this.h5 });
			},
			buttonStyle() {
				return {
					background: this.collectionData.buttonBg,
				};
			},
			metaText() {
				return getPayFileCollectionMetaText(this.collectionData);
			},
			unlockHint() {
				return getPayFileCollectionUnlockHint(this.collectionData);
			},
			totalCount() {
				return this.collectionData.items.length;
			},
			freePreviewCount() {
				if (this.compact) return PAY_FILE_COLLECTION_COMPACT_FREE_COUNT;
				return this.collectionData.freePreviewCount;
			},
			isUnlocked() {
				if (this.editable) return false;
				return this.unlockedLocal || isPayFileCollectionUnlocked(this.blockId) || this.isGeditModulePaid;
			},
			isUnfolded() {
				return this.unfoldedLocal;
			},
			listEntries() {
				return getPayFileCollectionListEntries(this.collectionData, {
					unlocked: this.isUnlocked,
					compact: this.compact,
					unfolded: this.isUnfolded,
				});
			},
			visibleEntries() {
				if (this.isUnfolded) {
					return this.listEntries;
				}
				const expandCount = this.collectionData.initExpandCount;
				if (!expandCount) return this.listEntries;
				return this.listEntries.slice(0, expandCount);
			},
			showInfoBar() {
				if (this.compact) return false;
				return this.totalCount > 0
					&& this.collectionData.freePreviewCount > 0
					&& this.collectionData.freePreviewCount < this.totalCount;
			},
		},
		watch: {
			blockId: {
				immediate: true,
				handler() {
					this.syncUnlockState();
				},
			},
		},
		methods: {
			syncUnlockState() {
				this.unlockedLocal = isPayFileCollectionUnlocked(this.blockId);
			},
			formatIndex(index) {
				return formatPayFileCollectionIndex(index);
			},
			getItemTone(item) {
				return item.tone || getPayFileCollectionFileType(item.filename).tone;
			},
			getItemMark(item) {
				return getPayFileCollectionFileType(item.filename).mark;
			},
			getItemIconUrl(item) {
				return getPayFileCollectionFileIconUrl(item.filename);
			},
			getItemMeta(item) {
				return getPayFileCollectionItemMeta(item);
			},
			featureIconName(icon) {
				const map = {
					shield: 'mbm-iconfont mbm-anquan',
					download: 'mbm-iconfont mbm-xiazai1',
					valid: 'mbm-iconfont mbm-check',
				};
				return map[icon] || map.shield;
			},
			canViewItem(entry) {
				if (this.editable || entry.locked) return false;
				return Boolean(getPayFileCollectionFileUrl(entry.item));
			},
		onWrapClick() {
			if (this.editable) {
				this.$emit('select');
			}
		},
			onUnlockClick() {
				this.runGeditUnlockFlow({
					applyUnlock: () => {
						setPayFileCollectionUnlocked(this.blockId, true);
						this.unlockedLocal = true;
					},
				});
			},
			onViewItem(entry, event) {
				if (!this.canViewItem(entry)) return;
				event.stopPropagation();
				const url = getPayFileCollectionFileUrl(entry.item);
				if (!url) return;
				window.open(url, '_blank');
			},
			onFoldToggle() {
				this.unfoldedLocal = !this.unfoldedLocal;
			},
			onItemClick(entry) {
				if (this.editable) {
					this.$emit('select');
					return;
				}
				if (entry.locked) {
					this.onUnlockClick();
				} else if (this.canViewItem(entry)) {
					this.onViewItem(entry, { stopPropagation: () => {} });
				}
			},
		},
	};
</script>

<style scoped lang="less">
	.g-block-pay-file-collection {
		box-sizing: border-box;

		&--edit {
			border: 1px solid transparent;
			border-radius: 4px;
			transition: border-color 0.2s, box-shadow 0.2s;
			cursor: pointer;

			&.is-active {
				border-color: #285af9;
				box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15);
			}
		}
	}

	.g-block-pay-file-collection__card {
		background: #fff;
		overflow: hidden;
		box-shadow: 0 0 12px rgba(0, 0, 0, 0.1);
	}

	.g-block-pay-file-collection__header {
		display: flex;
		align-items: center;
		gap: 14px;
		padding: 18px 14px 14px;
	}

	.g-block-pay-file-collection__folder {
		flex-shrink: 0;
		width: 52px;
		height: 52px;
		position: relative;
	}

	.g-block-pay-file-collection__folder-body {
		position: relative;
		width: 100%;
		height: 100%;
		border-radius: 10px ;
	}

	.g-block-pay-file-collection__folder-icon {
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	.g-block-pay-file-collection__folder-doc {
		display: none;
	}

	.g-block-pay-file-collection__header-info {
		flex: 1;
		min-width: 0;
	}

	.g-block-pay-file-collection__title {
		font-size: 18px;
		font-weight: 700;
		color: #1a1a1a;
		line-height: 1.35;
		word-break: break-all;
	}

	.g-block-pay-file-collection__meta {
		margin-top: 6px;
		font-size: 13px;
		color: #999;
		line-height: 1.4;
	}

	.g-block-pay-file-collection__info {
		display: flex;
		align-items: center;
		gap: 8px;
		margin: 0 14px 12px;
		padding: 10px 12px;
		border-radius: 10px;
		background: #eef4ff;
		font-size: 12px;
		color: #666;
		line-height: 1.5;

		strong {
			color: #285af9;
			font-weight: 600;
		}
	}

	.g-block-pay-file-collection__info-icon {
		flex-shrink: 0;
		font-size: 14px;
		color: #285af9;
	}

	.g-block-pay-file-collection__list {
		display: flex;
		flex-direction: column;
		gap: 10px;
		padding: 0 14px 14px;
	}
	.g-block-pay-file-collection__fold-btn-container{
		padding: 0 14px 14px;
	}
	.g-block-pay-file-collection__fold-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 4px;
		width: 100%;
		padding: 7px 10px;
		border: 1px solid #e8e8e8;
		border-radius: 8px;
		background: #f0f0f0;
		font-size: 13px;
		color: #666;
		cursor: pointer;
		transition: background-color 0.2s;
		box-sizing: border-box;

		&:hover {
			background-color: #f4f7fd;
		}
		.mbm-xiajiantou1111{
			margin-top: 2px;
			font-size: 14px;
		}
	}

	.g-block-pay-file-collection__item {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 12px;
		border: 1px solid #eee;
		border-radius: 12px;
		background: #fff;
		box-sizing: border-box;

		&.is-locked {
			.g-block-pay-file-collection__file-icon {
				filter: grayscale(1);
				opacity: 0.55;
			}

			.g-block-pay-file-collection__item-title {
				color: #1a1a1a;
			}

			.g-block-pay-file-collection__item-index {
				background: #d9d9d9;
				color: #fff;
			}
		}

		&:not(.is-locked) {
			.g-block-pay-file-collection__item-index {
				background: #285af9;
				color: #fff;
			}
		}
	}

	.g-block-pay-file-collection__file-icon {
		flex-shrink: 0;
		width: 42px;
		height: 42px;
		border-radius: 10px;
		display: flex;
		align-items: center;
		justify-content: center;

		// &.is-word {
		// 	box-shadow: 0 0px 10px rgba(40, 90, 249, .3);
		// }

		// &.is-powerpoint {
		// 	box-shadow: 0 0px 10px #ff65234d;
		// }
		// &.is-powerpoint {
		// 	box-shadow: 0 0px 10px #ff65234d;
		// }
		// &.is-ppt {
		// 	box-shadow: 0 0px 10px #ff65234d;
		// }
		// &.is-pdf {
		// 	box-shadow: 0 0px 10px #ec34494d;
		// }

		// &.is-txt {
		// 	box-shadow: 0 4px 10px #818ea44d
		// }

		// &.is-excel {
		// 	box-shadow: 0 4px 10px #1ab36e4d
		// }

		// &.is-file {
		// 	box-shadow: 0 4px 10px #7a4ceb4d
		// }
	}

	.g-block-pay-file-collection__file-icon-img {
		width: 40px;
		height: 40px;
		object-fit: contain;
	}

	.g-block-pay-file-collection__item-main {
		flex: 1;
		min-width: 0;
	}

	.g-block-pay-file-collection__item-head {
		display: flex;
		align-items: center;
		gap: 8px;
		min-width: 0;
	}

	.g-block-pay-file-collection__item-index {
		flex-shrink: 0;
		min-width: 28px;
		padding: 2px 6px;
		border-radius: 6px;
		background: #285af9;
		color: #fff;
		font-size: 11px;
		font-weight: 600;
		line-height: 1.3;
		text-align: center;
	}

	.g-block-pay-file-collection__item-title {
		flex: 1;
		min-width: 0;
		font-size: 15px;
		font-weight: 600;
		color: #1a1a1a;
		line-height: 1.35;
		word-break: break-all;
	}

	.g-block-pay-file-collection__item-meta {
		margin-top: 4px;
		font-size: 12px;
		color: #999;
		line-height: 1.4;
	}

	.g-block-pay-file-collection__free-tag {
		display: inline-flex;
		margin-top: 6px;
		padding: 1px 8px;
		border-radius: 999px;
		background: #f6ffed;
		color: #52c41a;
		font-size: 11px;
		line-height: 1.5;
	}

	.g-block-pay-file-collection__item-action {
		flex-shrink: 0;
	}

	.g-block-pay-file-collection__view-btn {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		padding: 0;
		border: none;
		background: transparent;
		color: #285af9;
		font-size: 13px;
		line-height: 1.4;
		cursor: pointer;
		outline: none;
		white-space: nowrap;

		:deep(.anticon) {
			font-size: 12px;
		}

		&.is-readonly {
			cursor: default;
			opacity: 0.65;
		}
	}

	.g-block-pay-file-collection__lock-btn {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		padding: 6px 10px;
		border: none;
		border-radius: 999px;
		background: #e9e9e9;
		color: #1a1a1a;
		font-size: 12px;
		line-height: 1.3;
		cursor: pointer;
		outline: none;
		white-space: nowrap;

		.mbm-iconfont {
			font-size: 12px;
			color: #1a1a1a;
		}
	}

	.g-block-pay-file-collection__pay {
		margin: 0 14px 14px;
		padding: 14px;
		border-radius: 12px;
		background: #f7f8fa;
	}

	.g-block-pay-file-collection__pay-main {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
	}

	.g-block-pay-file-collection__price {
		display: flex;
		align-items: baseline;
		color: #ff4d4f;
		line-height: 1;
	}

	.g-block-pay-file-collection__currency {
		font-size: 14px;
		font-weight: 600;
		margin-right: 2px;
	}

	.g-block-pay-file-collection__price-value {
		font-size: 28px;
		font-weight: 700;
	}

	.g-block-pay-file-collection__pay-desc {
		margin-top: 6px;
		font-size: 12px;
		color: #999;
		line-height: 1.4;
	}

	.g-block-pay-file-collection__unlock-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		flex-shrink: 0;
		border: none;
		border-radius: 999px;
		padding: 10px 16px;
		font-size: 13px;
		font-weight: 600;
		color: #fff;
		cursor: pointer;
		line-height: 1.2;
		white-space: nowrap;

		.mbm-iconfont {
			font-size: 15px;
		}

		&:active {
			opacity: 0.92;
		}
	}

	.g-block-pay-file-collection__features {
		display: flex;
		justify-content: space-around;
		flex-wrap: wrap;
		gap: 10px 16px;
		margin-top: 12px;
		padding-top: 12px;
		border-top: 1px solid #eceef2;
	}

	.g-block-pay-file-collection__feature {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		font-size: 11px;
		color: #666;
		line-height: 1.3;

		&.is-shield i {
			font-size: 12px;
			color: #52c41a;
		}

		&.is-download i {
			font-size: 11px;
			color: #285af9;
		}

		&.is-valid i {
			font-size: 11px;
			color: #faad14;
		}
	}

	.g-block-pay-file-collection--compact {
		.g-block-pay-file-collection__header {
			padding: 12px 10px 10px;
			gap: 10px;
		}

		.g-block-pay-file-collection__folder {
			width: 42px;
			height: 42px;
		}

		.g-block-pay-file-collection__folder-doc {
			display: none;
		}

		.g-block-pay-file-collection__title {
			font-size: 15px;
		}

		.g-block-pay-file-collection__meta {
			font-size: 11px;
		}

		.g-block-pay-file-collection__list {
			gap: 8px;
			padding: 0 10px 10px;
		}

		.g-block-pay-file-collection__item {
			padding: 10px;
			gap: 8px;
		}

		.g-block-pay-file-collection__file-icon {
			width: 40px;
			height: 40px;
		}

		.g-block-pay-file-collection__item-title {
			font-size: 13px;
		}

		.g-block-pay-file-collection__item-meta,
		.g-block-pay-file-collection__free-tag {
			padding-left: 0;
			margin-left: 0;
			font-size: 10px;
		}


	

		.g-block-pay-file-collection__view-btn,
		.g-block-pay-file-collection__lock-btn {
			font-size: 11px;
			padding: 5px 8px;
		}

		.g-block-pay-file-collection__pay {
			margin: 0 10px 10px;
			padding: 10px;
		}

		.g-block-pay-file-collection__price-value {
			font-size: 22px;
		}

		.g-block-pay-file-collection__unlock-btn {
			padding: 10px 12px;
			font-size: 13px;
		}

		.g-block-pay-file-collection__features {
			gap: 8px 12px;
			margin-top: 8px;
			padding-top: 8px;
		}

		.g-block-pay-file-collection__feature {
			font-size: 10px;
		}
	}

	.g-block-pay-file-collection--h5 {
		.g-block-pay-file-collection__header {
			gap: 0.28rem;
			padding: 0.36rem 0.28rem 0.28rem;
		}

		.g-block-pay-file-collection__folder {
			width: 1.04rem;
			height: 1.04rem;
		}

		.g-block-pay-file-collection__folder-body {
			border-radius: 0.2rem 0.2rem 0.16rem 0.16rem;

			&::before {
				top: -0.12rem;
				width: 0.48rem;
				height: 0.16rem;
				border-radius: 0.12rem 0.12rem 0 0;
			}
		}

		.g-block-pay-file-collection__folder-doc {
			display: none;
		}

		.g-block-pay-file-collection__title {
			font-size: 0.36rem;
		}

		.g-block-pay-file-collection__meta {
			margin-top: 0.12rem;
			font-size: 0.26rem;
		}

		.g-block-pay-file-collection__info {
			gap: 0.16rem;
			margin: 0 0.28rem 0.24rem;
			padding: 0.2rem 0.24rem;
			border-radius: 0.2rem;
			font-size: 0.24rem;
		}

		.g-block-pay-file-collection__info-icon {
			font-size: 0.28rem;
		}

		.g-block-pay-file-collection__list {
			gap: 0.2rem;
			padding: 0 0.28rem 0.28rem;
		}

		.g-block-pay-file-collection__item {
			gap: 0.24rem;
			padding: 0.24rem;
			border-radius: 0.24rem;
		}

		.g-block-pay-file-collection__fold-btn {
			padding: 0.14rem 0.24rem;
			border-radius: 0.16rem;
			font-size: 0.26rem;
			border: 1px solid #e8e8e8;
			.mbm-xiajiantou1111{
				margin-top: 0.04rem;
				font-size: 0.28rem;
			}
		}

		.g-block-pay-file-collection__file-icon {
			width: 0.8rem;
			height: 0.8rem;
			border-radius: 0.2rem;
		}

		.g-block-pay-file-collection__file-icon-img {
			width: 0.8rem;
			height: 0.8rem;
		}

		.g-block-pay-file-collection__item-head {
			gap: 0.16rem;
		}

		.g-block-pay-file-collection__item-index {
			min-width: 0.56rem;
			padding: 0.04rem 0.12rem;
			border-radius: 0.12rem;
			font-size: 0.22rem;
		}

		.g-block-pay-file-collection__item-title {
			font-size: 0.26rem;
		}

		.g-block-pay-file-collection__item-meta {
			margin-top: 0.08rem;
			font-size: 0.20rem;
		}

		.g-block-pay-file-collection__free-tag {
			margin-top: 0.12rem;
			padding: 0.02rem 0.16rem;
			font-size: 0.20rem;
		}

		.g-block-pay-file-collection__view-btn,
		.g-block-pay-file-collection__lock-btn {
			font-size: 0.26rem;
		}

		.g-block-pay-file-collection__lock-btn {
			padding: 0.12rem 0.2rem;

			.mbm-iconfont {
				font-size: 0.24rem;
			}
		}

		.g-block-pay-file-collection__pay {
			margin: 0 0.28rem 0.28rem;
			padding: 0.28rem;
			border-radius: 0.24rem;
		}

		.g-block-pay-file-collection__pay-main {
			gap: 0.24rem;
		}

		.g-block-pay-file-collection__currency {
			font-size: 0.28rem;
		}

		.g-block-pay-file-collection__price-value {
			font-size: 0.56rem;
		}

		.g-block-pay-file-collection__pay-desc {
			margin-top: 0.12rem;
			font-size: 0.24rem;
		}

		.g-block-pay-file-collection__unlock-btn {
			gap: 0.12rem;
			padding: 0.2rem 0.32rem;
			font-size: 0.26rem;

			.mbm-iconfont {
				font-size: 0.3rem;
			}
		}

		.g-block-pay-file-collection__features {
			gap: 0.2rem 0.32rem;
			margin-top: 0.24rem;
			padding-top: 0.24rem;
		}

		.g-block-pay-file-collection__feature {
			gap: 0.08rem;
			font-size: 0.22rem;
		}
	}
</style>
